import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Select, DatePicker } from 'antd';
import dayjs from 'dayjs';
import {
  FiCheck, FiChevronLeft, FiUser, FiCreditCard,
  FiUpload, FiUploadCloud, FiX, FiAlertCircle,
  FiArrowRight, FiMail, FiPhone, FiBriefcase,
} from 'react-icons/fi';
import AutomatedCmrStartStep from './AutomatedCmrStartStep';
import { parseCmrDob } from '../../utils/cmrExtractionHelpers';
import { readCmrFileFromIndexedDB } from '../../utils/cmrUploadFileStore';
import { RiBuilding2Line } from 'react-icons/ri';
import { HiOutlineDocumentText } from 'react-icons/hi';
import investorOnboardingService from '../../services/investorOnboardingService';
import ifaService from '../../services/ifaService';
import DashboardPageWrapper from '../../components/layout/DashboardPageWrapper';
import {
  patchOnboardingState,
  clearLocalOnboardingState,
  backToMethodSelection,
  readStoredCmrExtraction,
  getStoredInvestorOnboardingType,
  inferInvestorOnboardingType,
  INVESTOR_ONBOARDING_TYPES,
} from '../../utils/investorOnboardingPersistence';
import { promotePendingCmrFileToSession } from '../../utils/cmrUploadFileStore';
import BankSourceChoiceModal from './components/BankSourceChoiceModal';
import DematSourceChoiceModal from './components/DematSourceChoiceModal';
import AlreadyOnboardedModal from './components/AlreadyOnboardedModal';
import CmrDocumentPreviewModal from './components/CmrDocumentPreviewModal';
import PartnerRemoteSelect from './components/PartnerRemoteSelect';
import {
  extractBankFromCmr,
  hasBankAccountAndIfsc,
  needsBankSourceResolution,
  normalizeExistingBanks,
} from '../../utils/bankStepPrefill';
import {
  applyBankManualUploadState,
  buildBankManualUploadState,
} from '../../utils/bankVerifyManualUpload';
import BankManualUploadErrorAlert from '../../components/bank/BankManualUploadErrorAlert';
import {
  extractDematFromCmr,
  hasUsableCmrDemat,
  needsDematSourceResolution,
  normalizeExistingDemats,
} from '../../utils/dematStepPrefill';
import { processSupportingDocumentForUpload } from '../../utils/cmrFileProcessing';
import {
  DEMAT_FORMAT_HINT,
  DEMAT_MAX_LENGTH,
  formatDematInput,
  getDematInputError,
  processDematInputChange,
  validateDematNumber,
} from '../../utils/dematValidation';

const PAN_REGEX = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
const IFSC_REGEX = /^[A-Z]{4}0[A-Z0-9]{6}$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const WIZARD_STEPS_TAIL = [
  { key: 'STEP_2_PAN', label: 'Personal Details', sublabel: 'PAN, contact', icon: FiUser },
  { key: 'STEP_3_BANK_DETAILS', label: 'Bank Verification', sublabel: 'Account details', icon: RiBuilding2Line },
  { key: 'STEP_4_DEMAT', label: 'Demat & Documents', sublabel: 'CMR upload', icon: FiCreditCard },
  { key: 'STEP_COMPLETED', label: 'Onboarding complete', sublabel: 'Investor ready', icon: FiCheck },
];

function getWizardSteps(isAutomated) {
  const step1 = isAutomated
    ? {
        key: 'STEP_1_PARTNER_SELECTION',
        label: 'CMR & Partner Setup',
        sublabel: 'Upload CMR, partner & mobile',
        icon: FiUploadCloud,
      }
    : {
        key: 'STEP_1_PARTNER_SELECTION',
        label: 'Partner Selection',
        sublabel: 'Select partner & start session',
        icon: FiBriefcase,
      };
  return [step1, ...WIZARD_STEPS_TAIL];
}

function mapApiStepToWizard(apiStep) {
  if (!apiStep) return 'STEP_2_PAN';
  if (apiStep === 1) return 'STEP_2_PAN'; // Handle ghost sessions that started at step 1
  if (apiStep === 'COMPLETED') return 'STEP_COMPLETED';
  return apiStep;
}

function mapCompletedStepKey(key) {
  if (key === 'COMPLETED') return 'STEP_COMPLETED';
  return key;
}

function InputField({ label, error, className = '', children }) {
  return (
    <div className={`flex flex-col gap-1 ${className}`}>
      {label && <label className="text-sm font-medium text-gray-700">{label}</label>}
      {children}
      {error && (
        <p className="text-xs text-red-500 flex items-center gap-1">
          <FiAlertCircle size={12} />
          {error}
        </p>
      )}
    </div>
  );
}

function StyledInput({ value, onChange, placeholder, disabled, className = '', type = 'text', maxLength, autoFocus }) {
  return (
    <input
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      disabled={disabled}
      maxLength={maxLength}
      autoFocus={autoFocus}
      className={`w-full px-3.5 py-2.5 rounded-lg border text-sm transition-all outline-none
        ${disabled
          ? 'bg-gray-50 text-gray-500 border-gray-200 cursor-not-allowed'
          : 'bg-white text-gray-900 border-gray-300 hover:border-green-400 focus:border-green-500 focus:ring-2 focus:ring-green-500/10'
        } ${className}`}
    />
  );
}

function GreenButton({ onClick, disabled, loading, children, className = '', type = 'button' }) {
  const inactive = disabled || loading;
  const stateClass = loading
    ? 'bg-green-600 text-white cursor-wait shadow-sm'
    : disabled
      ? 'bg-gray-200 text-gray-500 cursor-not-allowed shadow-none'
      : 'bg-green-600 text-white hover:bg-green-700 active:scale-[0.99] shadow-sm hover:shadow-md';

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={inactive}
      className={`w-full py-2.5 px-4 rounded-lg font-semibold text-sm transition-all ${stateClass} ${className}`}
    >
      {loading ? (
        <span className="flex items-center justify-center gap-2">
          <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" aria-hidden>
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
          </svg>
          Processing...
        </span>
      ) : (
        children
      )}
    </button>
  );
}

function OnboardingWizardLayout({
  children,
  steps,
  completedStepKeys,
  currentStepKey,
  maxReachedKey,
  contextLabel,
  percentComplete,
  expiresAt,
  onBackToList,
  onStepClick,
  title = 'Investor onboarding',
}) {
  return (
    <DashboardPageWrapper>
      <button
        type="button"
        onClick={onBackToList}
        className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 mb-5 transition-colors"
      >
        <FiChevronLeft size={16} /> Back to investor list
      </button>

      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="flex flex-col md:flex-row md:min-h-[540px]">
          {/* Sidebar */}
          <div className="shrink-0 border-b border-gray-100 bg-gray-50/70 p-5 md:w-52 md:border-b-0 md:border-r md:px-4 md:py-6 lg:w-56">
            <WizardSidebar
              steps={steps}
              completedStepKeys={completedStepKeys}
              currentStepKey={currentStepKey}
              maxReachedKey={maxReachedKey}
              contextLabel={contextLabel}
              percentComplete={percentComplete}
              expiresAt={expiresAt}
              title={title}
              onStepClick={onStepClick}
            />
          </div>

          {/* Main content panel */}
          <div className="flex min-w-0 flex-1 flex-col items-stretch justify-center p-4 sm:p-5 md:items-center md:px-6 md:py-10 lg:px-8">
            <div className="w-full min-w-0 max-w-lg rounded-xl border border-gray-100 bg-gray-50/60 p-4 shadow-sm sm:p-6">
              {children}
            </div>
          </div>
        </div>
      </div>
    </DashboardPageWrapper>
  );
}

function WizardSidebar({ steps, completedStepKeys, currentStepKey, maxReachedKey, contextLabel, percentComplete, onStepClick, title }) {
  const maxIdx = steps.findIndex((s) => s.key === maxReachedKey);

  return (
    <div className="flex flex-col">
      {/* Header */}
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-widest text-green-600">Admin Dashboard</p>
        <h2 className="mt-1 text-xl font-semibold text-gray-900 leading-tight">{title}</h2>
        {contextLabel && (
          <p className="mt-1.5 text-sm leading-relaxed text-gray-500 truncate">Partner: {contextLabel}</p>
        )}
      </div>

      {/* Progress bar */}
      {percentComplete > 0 && (
        <div className="mt-3">
          <div className="h-1 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-green-500 rounded-full transition-all duration-700"
              style={{ width: `${percentComplete}%` }}
            />
          </div>
        </div>
      )}

      {/* Mobile: horizontal pills */}
      <div className="mt-4 flex flex-wrap gap-1.5 md:hidden">
        {steps.map((step, idx) => {
          const isCompleted = completedStepKeys.includes(step.key);
          const isActive = step.key === currentStepKey;
          const isClickable = onStepClick && maxIdx >= 0 && idx <= maxIdx;
          return (
            <button
              key={step.key}
              type="button"
              onClick={() => isClickable && onStepClick(step.key)}
              disabled={!isClickable}
              className={`rounded-full px-2.5 py-1 text-xs font-medium transition-colors ${
                isActive
                  ? 'bg-green-100 text-green-700'
                  : isCompleted
                    ? 'bg-emerald-100 text-emerald-700'
                    : 'bg-gray-100 text-gray-400'
              }`}
            >
              {step.label}
            </button>
          );
        })}
      </div>

      {/* Desktop: connected stepper */}
      <div className="mt-8 hidden md:flex md:flex-col">
        {steps.map((step, idx) => {
          const isCompleted = completedStepKeys.includes(step.key);
          const isActive = step.key === currentStepKey;
          const isLast = idx === steps.length - 1;
          const isClickable = onStepClick && maxIdx >= 0 && idx <= maxIdx;

          const circleClass = `flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold transition-all ${
            isActive
              ? 'bg-green-600 text-white ring-4 ring-green-600/15'
              : isCompleted
                ? 'bg-emerald-500 text-white'
                : 'border-2 border-gray-300 bg-white text-gray-400'
          }`;
          const labelClass = `block text-sm font-medium leading-tight ${
            isActive ? 'text-green-700' : isCompleted ? 'text-gray-700' : 'text-gray-400'
          }`;

          const goToStep = () => { if (isClickable) onStepClick(step.key); };

          return (
            <div key={step.key} className="flex gap-3.5">
              {/* Circle + connector line */}
              <div className="flex flex-col items-center">
                <button
                  type="button"
                  onClick={goToStep}
                  disabled={!isClickable}
                  aria-label={`${step.label}: ${step.sublabel}`}
                  className={`${circleClass} ${isClickable ? 'cursor-pointer' : 'cursor-default'}`}
                >
                  {isCompleted ? <FiCheck size={12} /> : <span>{idx + 1}</span>}
                </button>
                {!isLast && (
                  <div
                    className={`my-1 w-0.5 flex-1 rounded-full ${isCompleted ? 'bg-emerald-300' : 'bg-gray-200'}`}
                    style={{ minHeight: 28 }}
                    aria-hidden="true"
                  />
                )}
              </div>

              {/* Label + sublabel */}
              <button
                type="button"
                onClick={goToStep}
                disabled={!isClickable}
                className={`flex-1 text-left pt-0.5 ${isLast ? 'pb-0' : 'pb-5'} ${isClickable ? 'cursor-pointer' : 'cursor-default'}`}
              >
                <span className={labelClass}>{step.label}</span>
                <span className={`mt-0.5 block text-xs ${!isClickable ? 'text-gray-300' : 'text-gray-400'}`}>
                  {step.sublabel}
                </span>
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function IFAStartStep({
  selectedIfaId,
  onSelectIfa,
  mobile,
  onMobileChange,
  onStart,
  starting,
  isAutomated = false,
}) {
  if (isAutomated) {
    return (
      <AutomatedCmrStartStep
        selectedIfaId={selectedIfaId}
        onSelectIfa={onSelectIfa}
        mobile={mobile}
        onMobileChange={onMobileChange}
        onStart={onStart}
        starting={starting}
      />
    );
  }

  const canStart = selectedIfaId && mobile.length === 10;

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900">Partner Selection</h3>
        <p className="text-sm text-gray-500 mt-0.5">
          Choose the partner this investor will sit under, then start the onboarding session.
        </p>
      </div>

      <div className="flex flex-col gap-5">
        <InputField label="Partner">
          <PartnerRemoteSelect
            size="large"
            className="w-full"
            value={selectedIfaId}
            onChange={onSelectIfa}
          />
        </InputField>

        <InputField label="Investor Mobile" className="mt-2 text-gray-900 border-gray-300">
          <StyledInput
            value={mobile}
            onChange={(e) => onMobileChange(e.target.value.replace(/\D/g, ''))}
            placeholder="Enter 10-digit mobile number"
            maxLength={10}
            className="mt-1"
          />
        </InputField>

        <GreenButton onClick={onStart} disabled={!canStart} loading={starting}>
          Start onboarding <FiArrowRight className="inline ml-1" />
        </GreenButton>
      </div>
    </div>
  );
}

function IFAUpdateMobileStep({ sessionUuid, contextLabel, mobile, onSuccess }) {
  const [newMobile, setNewMobile] = useState(mobile || '');
  const [loading, setLoading] = useState(false);

  const handleUpdate = async () => {
    if (newMobile.length !== 10) return;
    setLoading(true);
    try {
      await investorOnboardingService.updateMobile(sessionUuid, newMobile);
      toast.success('Mobile number updated.');
      onSuccess();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to update mobile.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900">Update Contact Details</h3>
        <p className="text-sm text-gray-500 mt-0.5">
          The partner is locked for this session. You can update the investor's mobile number below.
        </p>
      </div>

      <div className="flex flex-col gap-5">
        <InputField label="Partner">
          <StyledInput value={contextLabel} disabled placeholder="Partner" />
        </InputField>

        <InputField label="Investor Mobile">
          <StyledInput
            value={newMobile}
            onChange={(e) => setNewMobile(e.target.value.replace(/\D/g, ''))}
            placeholder="Enter 10-digit mobile number"
            maxLength={10}
            autoFocus
          />
        </InputField>

        <GreenButton onClick={handleUpdate} disabled={newMobile.length !== 10 || newMobile === mobile} loading={loading}>
          Save & Continue <FiCheck className="inline ml-1" />
        </GreenButton>
      </div>
    </div>
  );
}

function InvestorPersonalStep({ sessionUuid, sessionStatus, onSuccess, onSaveMobile, setPrefillData, cmrExtraction }) {
  const cmrPan = cmrExtraction?.pan || '';
  const cmrEmail = cmrExtraction?.email || '';
  const cmrDob = parseCmrDob(cmrExtraction?.dob);

  const [pan, setPan] = useState(sessionStatus?.pan || cmrPan);
  const [lookupName, setLookupName] = useState(sessionStatus?.name || '');
  const [investorName, setInvestorName] = useState(sessionStatus?.name || '');
  const [investorEmail, setInvestorEmail] = useState(
    sessionStatus?.email && !String(sessionStatus.email).startsWith('PEND_')
      ? sessionStatus.email
      : cmrEmail
  );
  const [dob, setDob] = useState(
    sessionStatus?.dob
      ? dayjs(sessionStatus.dob).format('YYYY-MM-DD')
      : cmrDob
  );
  const [mobile, setMobile] = useState(sessionStatus?.mobile || '');
  const [panLookupLoading, setPanLookupLoading] = useState(false);
  const [panVerified, setPanVerified] = useState(!!sessionStatus?.pan);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [isExisting, setIsExisting] = useState(false);
  const [alreadyOnboardedModal, setAlreadyOnboardedModal] = useState(null);

  const panValue = pan.toUpperCase();
  const isPanValid = PAN_REGEX.test(panValue);

  const lastPrefilledPan = useRef('');

  useEffect(() => {
    if (!isPanValid) {
      setLookupName('');
      setPanVerified(false);
      setIsExisting(false);
      setAlreadyOnboardedModal(null);
      setPrefillData(null);
      lastPrefilledPan.current = '';
      return;
    }

    // If we've already pre-filled for this specific PAN, don't trigger the lookup again.
    // This protects manual edits (email, dob) from being overwritten while typing.
    if (panValue === lastPrefilledPan.current) {
      return;
    }

    let cancelled = false;
    const lookup = async () => {
      setPanLookupLoading(true);
      setAlreadyOnboardedModal(null);
      try {
        const mobileDigits = String(mobile || sessionStatus?.mobile || cmrExtraction?.mobile || '')
          .replace(/\D/g, '');
        const mobileForCheck =
          mobileDigits.length >= 10 ? mobileDigits.slice(-10) : mobileDigits;

        const info = await investorOnboardingService.checkPan({
          pan: panValue,
          ifa_id: sessionStatus?.ifa_id,
          session_id: sessionUuid,
          mobile: mobileForCheck,
        });
        if (cancelled) return;

        if (info.exists) {
          if (info.already_onboarded) {
            setAlreadyOnboardedModal({
              message: info.message || `This investor is already onboarded under this partner.`,
              folioNumber: info.folio_number || info.folio_id || null,
              pan: panValue,
            });
            setPanVerified(false);
            return;
          }
          toast.success('Investor found! Details pre-filled.');
          // Backend returns name/email/dob either inside a `prefill` object (new-investor path)
          // or at the top level of data (global-user path). Support both shapes.
          const prefill = info.prefill || info;
          setLookupName(prefill.name || '');
          
          // When the PAN changes, we pre-fill. 
          // But we still protect manually edited fields if they already HAVE a non-placeholder value.
          setInvestorName((prev) => (prev && prev !== 'PENDING_ONBOARDING' ? prev : (prefill.name || '')));
          
          setInvestorEmail((prev) => {
              if (prev && !prev.startsWith('PEND_')) return prev;
              return prefill.email?.startsWith('PEND_') ? '' : (prefill.email || '');
          });
          
          const fetchedMobile = prefill.mobile?.startsWith('PEND_') ? '' : prefill.mobile;
          setMobile((prev) => {
              if (prev && !prev.startsWith('PEND_')) return prev;
              return fetchedMobile || sessionStatus?.mobile || '';
          });

          if (prefill.dob) {
            setDob((prev) => (prev ? prev : dayjs(prefill.dob).format('YYYY-MM-DD')));
          }

          setPanVerified(true);
          setIsExisting(true);
          setPrefillData(prefill);
          lastPrefilledPan.current = panValue;
        } else {
          // New investor
          setIsExisting(false);
          setPrefillData(null);
          const name = info.name || '';
          setLookupName(name);

          // Pre-fill name from KYC if available
          setInvestorName(name);
          setPanVerified(true);
          lastPrefilledPan.current = panValue;
        }
      } catch (err) {
        if (!cancelled) {
          setLookupName('');
          setPanVerified(false);
          toast.error(err?.response?.data?.message || 'Failed to verify PAN');
        }
      } finally {
        if (!cancelled) setPanLookupLoading(false);
      }
    };
    lookup();
    return () => {
      cancelled = true;
    };
  }, [panValue, isPanValid, sessionUuid, sessionStatus?.ifa_id, sessionStatus?.mobile, mobile, cmrExtraction?.mobile, setPrefillData]);

  const validate = () => {
    const e = {};
    if (!isPanValid) e.pan = 'Enter a valid PAN';
    
    // Always validate fields even if they are pre-filled, so updates are clean
    if (!investorName.trim()) e.investorName = 'Name is required';
    if (!investorEmail) e.investorEmail = 'Email is required';
    else if (!EMAIL_REGEX.test(investorEmail.trim())) e.investorEmail = 'Invalid email';
    if (!dob) e.dob = 'Date of birth is required';
    if (!mobile) e.mobile = 'Mobile is required';
    else if (!/^\d{10}$/.test(mobile)) e.mobile = 'Enter a valid 10-digit mobile number';
    
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate() || Boolean(alreadyOnboardedModal)) return;
    setLoading(true);
    try {
      const result = await investorOnboardingService.savePersonalDetails({
        session_id: sessionUuid,
        pan: panValue,
        dob,
        name: investorName.trim(),
        email: investorEmail.trim(),
        mobile,
      });
      // V2 flow continues to Step 3 even if isExisting, because they need to be linked 
      // via selecting a bank/demat or adding a new one.
      toast.success('Personal details saved!');
      onSaveMobile(mobile);
      onSuccess();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to save personal details.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <AlreadyOnboardedModal
        open={Boolean(alreadyOnboardedModal)}
        message={alreadyOnboardedModal?.message}
        folioNumber={alreadyOnboardedModal?.folioNumber}
        pan={alreadyOnboardedModal?.pan}
        onClose={() => setAlreadyOnboardedModal(null)}
        onGoToInvestors={() => {
          setAlreadyOnboardedModal(null);
          window.location.href = '/investors/list';
        }}
      />

      <div>
        <h3 className="text-lg font-semibold text-gray-900">Personal Details</h3>
        <p className="text-sm text-gray-500 mt-0.5">PAN, investor name, email, date of birth, and mobile.</p>
      </div>

      {isExisting && !alreadyOnboardedModal && (
        <div className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3.5 flex gap-3">
          <FiAlertCircle size={18} className="shrink-0 text-blue-500 mt-0.5" />
          <div className="text-sm text-blue-800">
            <p className="font-semibold">Investor already exists in the system</p>
            <p className="mt-0.5 text-blue-700">This investor is known but not linked to this partner yet. You can reuse their existing bank and demat accounts in the next steps.</p>
          </div>
        </div>
      )}

      <div className="flex flex-col gap-5">
        <InputField label="PAN number" error={errors.pan}>
          <div className="relative">
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
              <FiCreditCard size={16} />
            </div>
            <input
              value={panValue}
              onChange={(e) => {
                setPan(e.target.value.toUpperCase());
                setErrors((p) => ({ ...p, pan: '' }));
              }}
              placeholder="ABCDE1234F"
              maxLength={10}
              disabled={isExisting}
              className={`w-full pl-9 pr-10 py-2.5 rounded-lg border text-sm transition-all outline-none uppercase
                ${isExisting ? 'bg-gray-50 text-gray-500 hover:border-gray-300' : ''}
                ${errors.pan ? 'border-red-400' : 'border-gray-300 hover:border-green-400 focus:border-green-500 focus:ring-2 focus:ring-green-500/10'}`}
            />
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              {panLookupLoading && (
                <svg className="animate-spin h-4 w-4 text-green-500" fill="none" viewBox="0 0 24 24" aria-hidden>
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                </svg>
              )}
              {!panLookupLoading && panVerified && <FiCheck size={16} className="text-green-500" />}
            </div>
          </div>
          {panVerified && lookupName && (
            <p className="text-xs text-green-600 flex items-center gap-1 mt-1">
              <FiCheck size={12} /> Name on PAN: <span className="font-medium">{lookupName}</span>
            </p>
          )}
        </InputField>

        <InputField label="Investor name" error={errors.investorName}>
          <StyledInput
            value={investorName}
            onChange={(e) => {
              setInvestorName(e.target.value);
              setErrors((p) => ({ ...p, investorName: '' }));
            }}
            placeholder="Full name"
            disabled={!!lookupName}
          />
        </InputField>

        <InputField label="Email" error={errors.investorEmail}>
          <StyledInput
            type="email"
            value={investorEmail}
            onChange={(e) => {
              setInvestorEmail(e.target.value);
              setErrors((p) => ({ ...p, investorEmail: '' }));
            }}
            placeholder="investor@example.com"
          />
        </InputField>

        <InputField label="Date of birth" error={errors.dob}>
          <DatePicker
            size="large"
            className="w-full min-w-0"
            value={dob ? dayjs(dob, 'YYYY-MM-DD') : null}
            onChange={(d) => {
              setDob(d ? d.format('YYYY-MM-DD') : '');
              setErrors((p) => ({ ...p, dob: '' }));
            }}
            format="DD/MM/YYYY"
            placeholder="Select date of birth"
            disabledDate={(current) => current && current > dayjs().endOf('day')}
            status={errors.dob ? 'error' : undefined}
          />
        </InputField>

        <GreenButton onClick={handleSubmit} disabled={!isPanValid || Boolean(alreadyOnboardedModal)} loading={loading}>
          Continue
        </GreenButton>
      </div>
    </div>
  );
}

function isImageFile(file) {
  if (!file) return false;
  const mime = String(file.type || '').toLowerCase();
  if (mime.startsWith('image/')) return true;
  const name = String(file.name || '').trim().toLowerCase();
  return /\.(jpe?g|png|gif|webp|bmp|heic|heif)$/i.test(name);
}

function UploadedFilePreview({ file }) {
  const previewUrl = useMemo(() => {
    if (!file || !isImageFile(file)) return null;
    return URL.createObjectURL(file);
  }, [file]);

  useEffect(() => () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
  }, [previewUrl]);

  return (
    <div className="flex flex-col items-center gap-3 w-full">
      {previewUrl ? (
        <div className="w-full rounded-lg overflow-hidden border border-green-200/80 bg-gray-50 max-h-52 flex items-center justify-center">
          <img
            src={previewUrl}
            alt={`Preview: ${file.name}`}
            className="max-h-52 w-full object-contain"
          />
        </div>
      ) : (
        <div
          className="w-20 h-20 rounded-xl bg-green-100 border border-green-200/70 flex items-center justify-center"
          aria-hidden
        >
          <HiOutlineDocumentText className="w-10 h-10 text-green-700" />
        </div>
      )}
      <div className="text-center min-w-0 w-full">
        <p className="text-sm text-gray-700 font-medium break-all">{file.name}</p>
        <p className="text-xs text-gray-400 mt-0.5">{(file.size / 1024).toFixed(1)} KB</p>
      </div>
    </div>
  );
}

function InvestorBankStep({ sessionUuid, sessionStatus, mobile, onSuccess, prefillBanks = [], selectedBankId, cmrExtraction }) {
  const sessionBank = sessionStatus?.bank;
  const hasSessionBank = Boolean(sessionBank?.account_number || sessionBank?.ifsc_code);

  const existingBanks = useMemo(() => normalizeExistingBanks(prefillBanks), [prefillBanks]);
  const cmrBankFields = useMemo(() => extractBankFromCmr(cmrExtraction), [cmrExtraction]);
  const hasCmrBank = hasBankAccountAndIfsc(cmrBankFields);
  const hasPrefill = existingBanks.length > 0;

  // True when there are 2+ saved banks or 1 saved bank whose account differs from CMR
  const showConflict = useMemo(
    () => needsBankSourceResolution(existingBanks, cmrBankFields, sessionBank),
    [existingBanks, cmrBankFields, sessionBank]
  );

  // bankMode: 'existing' (calls selectBank) | 'mobile' | 'manual' (calls verifyBank)
  const [bankMode, setBankMode] = useState(() => {
    if (hasSessionBank) return 'manual';
    if (showConflict) return 'manual';
    if (hasPrefill) return 'existing';
    if (hasCmrBank) return 'manual';
    return 'mobile';
  });

  // Bank picked via modal (or auto-resolved when exactly 1 pool bank, no conflict)
  const [selectedBank, setSelectedBank] = useState(() => {
    if (hasSessionBank || showConflict) return null;
    const id = selectedBankId;
    return existingBanks.find((b) => b.bank_account_id === id) || existingBanks[0] || null;
  });

  const [bankSourceModalOpen, setBankSourceModalOpen] = useState(false);
  const modalAutoOpenedRef = useRef(false);

  // Auto-open conflict modal on first render when ambiguous sources exist
  useEffect(() => {
    if (!showConflict || modalAutoOpenedRef.current) return;
    modalAutoOpenedRef.current = true;
    setBankSourceModalOpen(true);
  }, [showConflict]);

  const [mobileBankData, setMobileBankData] = useState(null);
  const [mobileBankLoading, setMobileBankLoading] = useState(false);
  const [mobileBankError, setMobileBankError] = useState('');

  // Manual form fields: session-first, then CMR
  const [accountNumber, setAccountNumber] = useState(
    hasSessionBank ? (sessionBank?.account_number || '') : (cmrBankFields.account_number || '')
  );
  const [ifsc, setIfsc] = useState(
    hasSessionBank ? (sessionBank?.ifsc_code || '') : (cmrBankFields.ifsc || '')
  );
  const [bankName, setBankName] = useState(
    hasSessionBank ? (sessionBank?.bank_name || '') : (cmrBankFields.bank_name || '')
  );
  const [branchName, setBranchName] = useState('');
  const [ifscLookupLoading, setIfscLookupLoading] = useState(false);
  const [ifscVerified, setIfscVerified] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [mismatchData, setMismatchData] = useState(null);
  const [chequeFile, setChequeFile] = useState(null);
  const [otherDocFile, setOtherDocFile] = useState(null);
  const [chequeFileProcessing, setChequeFileProcessing] = useState(false);
  const [otherDocFileProcessing, setOtherDocFileProcessing] = useState(false);
  const chequeFileInputRef = useRef(null);
  const otherDocFileInputRef = useRef(null);

  const mobileDigits = /^\d{10}$/.test(mobile || '') ? mobile : '';

  useEffect(() => {
    if (!mobileDigits) return;
    setMobileBankLoading(true);
    setMobileBankError('');
    ifaService
      .getBankFromMobile(mobileDigits, sessionStatus?.name || null, sessionStatus?.investor_id)
      .then((res) => { setMobileBankData(res.data?.suggested_bank || null); })
      .catch(() => { setMobileBankError('Could not fetch bank details linked to mobile number.'); })
      .finally(() => setMobileBankLoading(false));
  }, [mobileDigits, sessionStatus?.name, sessionStatus?.investor_id]);

  const ifscValue = ifsc.toUpperCase();

  useEffect(() => {
    if (hasCmrBank && IFSC_REGEX.test(ifscValue) && bankName) {
      setIfscVerified(true);
    }
  }, [hasCmrBank, ifscValue, bankName]);

  useEffect(() => {
    if (bankMode !== 'manual') return;
    if (!IFSC_REGEX.test(ifscValue)) {
      setBankName('');
      setBranchName('');
      setIfscVerified(false);
      return;
    }
    let cancelled = false;
    const lookup = async () => {
      setIfscLookupLoading(true);
      try {
        const res = await ifaService.lookupIFSC(ifscValue);
        if (!cancelled) {
          setBankName(res.data?.bank_name || '');
          setBranchName(res.data?.branch_name || '');
          setIfscVerified(true);
        }
      } catch {
        if (!cancelled) { setBankName(''); setBranchName(''); setIfscVerified(false); }
      } finally {
        if (!cancelled) setIfscLookupLoading(false);
      }
    };
    lookup();
    return () => { cancelled = true; };
  }, [ifscValue, bankMode]);

  // Modal callbacks
  const handleUseExistingFromModal = (bank) => {
    setSelectedBank(bank);
    setBankMode('existing');
    setBankSourceModalOpen(false);
  };

  const handleUseCmrFromModal = () => {
    setAccountNumber(cmrBankFields.account_number || '');
    setIfsc(cmrBankFields.ifsc || '');
    setBankName(cmrBankFields.bank_name || '');
    setBranchName('');
    setIfscVerified(hasCmrBank && Boolean(cmrBankFields.bank_name));
    setBankMode('manual');
    setBankSourceModalOpen(false);
  };

  const handleUseNewFromModal = () => {
    setAccountNumber('');
    setIfsc('');
    setBankName('');
    setBranchName('');
    setIfscVerified(false);
    setBankMode('manual');
    setBankSourceModalOpen(false);
  };

  const handleRadioSaved = () => {
    if (showConflict || existingBanks.length >= 2) {
      setBankSourceModalOpen(true);
    } else {
      setBankMode('existing');
      setSelectedBank(existingBanks[0] || null);
    }
  };

  const validate = () => {
    const e = {};
    if (bankMode === 'existing') {
      if (!selectedBank) e.general = 'Please choose a bank account';
    } else if (bankMode === 'manual') {
      if (!accountNumber.trim()) e.accountNumber = 'Account number is required';
      if (!IFSC_REGEX.test(ifscValue)) e.ifsc = 'Enter a valid IFSC code';
      if (!bankName.trim()) e.bankName = 'Bank name is required (use IFSC lookup)';
    } else if (bankMode === 'mobile' && !mobileBankData) {
      e.general = mobileDigits
        ? 'Mobile-linked bank data not available. Please use manual entry.'
        : 'Enter a valid mobile on the previous step or use manual bank entry.';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const getAttemptedBankFields = () => {
    if (bankMode === 'existing' && selectedBank) {
      return {
        accountNumber: selectedBank.account_number,
        ifsc: selectedBank.ifsc_code || selectedBank.ifsc,
        bankName: selectedBank.bank_name,
      };
    }
    if (bankMode === 'mobile' && mobileBankData) {
      return {
        accountNumber: mobileBankData.account_number,
        ifsc: mobileBankData.ifsc_code,
        bankName: mobileBankData.bank_name,
        branchName: mobileBankData.branch_name || '',
      };
    }
    return {
      accountNumber,
      ifsc: ifscValue,
      bankName,
      branchName,
    };
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      if (bankMode === 'existing') {
        await investorOnboardingService.selectBank({
          session_id: sessionUuid,
          bank_account_id: selectedBank.bank_account_id,
        });
      } else {
        await investorOnboardingService.verifyBank({
          investor_id: sessionStatus?.investor_id,
          session_id: sessionUuid,
          account_number: bankMode === 'mobile' ? mobileBankData.account_number : accountNumber.trim(),
          ifsc: bankMode === 'mobile' ? mobileBankData.ifsc_code : ifscValue,
          bank_name: bankMode === 'mobile' ? mobileBankData.bank_name : bankName.trim(),
          branch_name: bankMode === 'mobile' ? (mobileBankData.branch_name || '') : (branchName || ''),
          beneficiary_name: bankMode === 'mobile' ? mobileBankData.name_as_per_bank : bankName.trim(),
        });
      }
      toast.success('Bank details saved!');
      onSuccess();
    } catch (err) {
      const manualUpload = buildBankManualUploadState(err, getAttemptedBankFields());
      applyBankManualUploadState(manualUpload, {
        setAccountNumber,
        setIfsc,
        setBankName,
        setBranchName,
        setBankMode,
        setMismatchData,
        setErrors,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleMismatchSubmit = async () => {
    if (!chequeFile) {
      setErrors({ cheque: 'Cancelled cheque / supporting document is required' });
      return;
    }
    if (chequeFileProcessing || otherDocFileProcessing) return;
    if (!accountNumber.trim()) {
      setErrors((p) => ({ ...p, accountNumber: 'Account number is required' }));
      return;
    }
    if (!IFSC_REGEX.test(ifscValue)) {
      setErrors((p) => ({ ...p, ifsc: 'Enter a valid IFSC code' }));
      return;
    }
    if (!bankName.trim()) {
      setErrors((p) => ({ ...p, bankName: 'Bank name is required (use IFSC lookup)' }));
      return;
    }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('session_uuid', sessionUuid);
      fd.append('account_number', accountNumber.trim());
      fd.append('ifsc_code', ifscValue);
      fd.append('bank_name', bankName.trim());
      fd.append('branch_name', branchName || '');
      fd.append('cheque', chequeFile);
      if (otherDocFile) fd.append('supporting_document', otherDocFile);
      await investorOnboardingService.uploadBankMismatchCheque(fd);
      toast.success('Documents submitted for review!');
      onSuccess();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to upload documents.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoBackFromMismatch = () => {
    setMismatchData(null);
    setChequeFile(null);
    setOtherDocFile(null);
    setChequeFileProcessing(false);
    setOtherDocFileProcessing(false);
    setErrors({});
  };

  const applyChequeFile = async (file) => {
    if (!file) return;
    setChequeFileProcessing(true);
    try {
      const result = await processSupportingDocumentForUpload(file);
      if (!result.ok) {
        toast.error(result.error);
        setErrors((p) => ({ ...p, cheque: result.error }));
        return;
      }
      setChequeFile(result.file);
      setErrors((p) => ({ ...p, cheque: '' }));
      if (result.wasCompressed) {
        toast.success('Image compressed to under 2 MB.');
      }
    } finally {
      setChequeFileProcessing(false);
    }
  };

  const applyOtherDocFile = async (file) => {
    if (!file) return;
    setOtherDocFileProcessing(true);
    try {
      const result = await processSupportingDocumentForUpload(file);
      if (!result.ok) {
        toast.error(result.error);
        return;
      }
      setOtherDocFile(result.file);
      if (result.wasCompressed) {
        toast.success('Image compressed to under 2 MB.');
      }
    } finally {
      setOtherDocFileProcessing(false);
    }
  };

  const bankRadioOptions = [];
  if (hasPrefill) {
    bankRadioOptions.push({
      value: 'existing',
      label: showConflict
        ? existingBanks.length >= 2
          ? `Select from ${existingBanks.length} saved accounts${hasCmrBank ? ' or CMR' : ''}`
          : 'Resolve account conflict — saved vs CMR'
        : existingBanks.length >= 2
          ? `Select from ${existingBanks.length} saved accounts`
          : 'Use a saved bank account',
      onClick: handleRadioSaved,
    });
  }
  bankRadioOptions.push({
    value: 'mobile',
    label: 'Use primary account linked to investor mobile',
    onClick: () => setBankMode('mobile'),
  });
  bankRadioOptions.push({
    value: 'manual',
    label: hasCmrBank && !hasSessionBank
      ? 'Enter account manually (CMR-extracted details pre-filled)'
      : 'Enter account manually',
    onClick: () => setBankMode('manual'),
  });

  return (
    <div className="flex flex-col gap-6">
      <BankSourceChoiceModal
        open={bankSourceModalOpen}
        existingBanks={existingBanks}
        cmrFields={cmrBankFields}
        hasCmrBank={hasCmrBank}
        onUseExisting={handleUseExistingFromModal}
        onUseCmr={handleUseCmrFromModal}
        onUseNew={handleUseNewFromModal}
      />

      <div>
        <h3 className="text-lg font-semibold text-gray-900">Bank Verification</h3>
        <p className="text-sm text-gray-500 mt-0.5">
          {mismatchData
            ? 'Bank verification could not be completed automatically. Upload documents for manual review.'
            : showConflict
              ? 'Multiple account sources found. Choose the one to use, or enter details manually.'
              : 'Use the mobile-linked account or enter details manually.'}
        </p>
      </div>

      {mismatchData ? (
        <div className="flex flex-col gap-5">
          <BankManualUploadErrorAlert manualUploadData={mismatchData} />

          <div className="rounded-xl border border-gray-200 p-5 flex flex-col gap-4">
            <InputField label="Account number" error={errors.accountNumber}>
              <StyledInput
                value={accountNumber}
                onChange={(e) => {
                  setAccountNumber(e.target.value);
                  setErrors((p) => ({ ...p, accountNumber: '' }));
                }}
                placeholder="Enter account number"
              />
            </InputField>

            <InputField label="IFSC" error={errors.ifsc}>
              <div className="relative">
                <input
                  value={ifscValue}
                  onChange={(e) => {
                    setIfsc(e.target.value.toUpperCase());
                    setErrors((p) => ({ ...p, ifsc: '' }));
                  }}
                  placeholder="e.g. HDFC0001234"
                  maxLength={11}
                  className={`w-full px-3.5 pr-10 py-2.5 rounded-lg border text-sm transition-all outline-none uppercase
                    ${errors.ifsc ? 'border-red-400' : 'border-gray-300 hover:border-green-400 focus:border-green-500 focus:ring-2 focus:ring-green-500/10'}`}
                />
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  {ifscLookupLoading && (
                    <svg className="animate-spin h-4 w-4 text-green-500" fill="none" viewBox="0 0 24 24" aria-hidden>
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                    </svg>
                  )}
                  {!ifscLookupLoading && ifscVerified && <FiCheck size={16} className="text-green-500" />}
                </div>
              </div>
            </InputField>

            <InputField label="Bank name" error={errors.bankName}>
              <StyledInput
                value={bankName}
                placeholder={ifscLookupLoading ? 'Fetching from IFSC...' : 'Auto-filled from IFSC lookup'}
                disabled
              />
            </InputField>

            {branchName && (
              <div className="bg-green-50 border border-green-200 rounded-lg px-3.5 py-2.5 text-sm text-green-700">
                Branch: <span className="font-medium">{branchName}</span>
              </div>
            )}
          </div>

          <div>
            <p className="text-sm font-medium text-gray-700 mb-1">
              Cancelled cheque / supporting document <span className="text-red-500">*</span>
            </p>
            {errors.cheque && (
              <p className="text-xs text-red-500 flex items-center gap-1 mb-1">
                <FiAlertCircle size={12} /> {errors.cheque}
              </p>
            )}
            <div
              role="button"
              tabIndex={0}
              onClick={() => !loading && !chequeFileProcessing && chequeFileInputRef.current?.click()}
              onKeyDown={(e) => e.key === 'Enter' && !chequeFileProcessing && chequeFileInputRef.current?.click()}
              className={`border-2 border-dashed rounded-lg p-4 flex flex-col items-center gap-2 transition-all
                ${chequeFileProcessing ? 'border-gray-200 bg-gray-50 cursor-wait opacity-80' : 'cursor-pointer'}
                ${!chequeFileProcessing && (chequeFile ? 'border-green-400 bg-green-50/50' : 'border-gray-200 bg-gray-50 hover:border-green-400 hover:bg-green-50/30')}`}
            >
              {chequeFileProcessing ? (
                <>
                  <svg className="animate-spin h-5 w-5 text-green-500" fill="none" viewBox="0 0 24 24" aria-hidden>
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                  </svg>
                  <p className="text-sm text-gray-500">Processing file...</p>
                </>
              ) : chequeFile ? (
                <UploadedFilePreview file={chequeFile} />
              ) : (
                <>
                  <FiUpload size={20} className="text-gray-400" />
                  <p className="text-sm text-gray-500">Click to select file</p>
                  <p className="text-xs text-gray-400">PDF max 2 MB · Images up to 5 MB (auto-compressed to 2 MB)</p>
                </>
              )}
            </div>
            <input
              ref={chequeFileInputRef}
              type="file"
              className="hidden"
              accept=".pdf,.jpg,.jpeg,.png"
              disabled={chequeFileProcessing}
              onChange={(e) => {
                const f = e.target.files?.[0];
                e.target.value = '';
                if (f) applyChequeFile(f);
              }}
            />
            {chequeFile && !chequeFileProcessing && (
              <button
                type="button"
                onClick={() => setChequeFile(null)}
                className="mt-2 text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1"
              >
                <FiX size={12} /> Remove file
              </button>
            )}
          </div>

          <div>
            <p className="text-sm font-medium text-gray-700 mb-1">Other supporting documents (optional)</p>
            <div
              role="button"
              tabIndex={0}
              onClick={() => !loading && !otherDocFileProcessing && otherDocFileInputRef.current?.click()}
              onKeyDown={(e) => e.key === 'Enter' && !otherDocFileProcessing && otherDocFileInputRef.current?.click()}
              className={`border-2 border-dashed rounded-lg p-4 flex flex-col items-center gap-2 transition-all
                ${otherDocFileProcessing ? 'border-gray-200 bg-gray-50 cursor-wait opacity-80' : 'cursor-pointer'}
                ${!otherDocFileProcessing && (otherDocFile ? 'border-green-400 bg-green-50/50' : 'border-gray-200 bg-gray-50 hover:border-green-400 hover:bg-green-50/30')}`}
            >
              {otherDocFileProcessing ? (
                <>
                  <svg className="animate-spin h-5 w-5 text-green-500" fill="none" viewBox="0 0 24 24" aria-hidden>
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                  </svg>
                  <p className="text-sm text-gray-500">Processing file...</p>
                </>
              ) : otherDocFile ? (
                <UploadedFilePreview file={otherDocFile} />
              ) : (
                <>
                  <FiUpload size={20} className="text-gray-400" />
                  <p className="text-sm text-gray-500">Click to select file</p>
                  <p className="text-xs text-gray-400">PDF max 2 MB · Images up to 5 MB (auto-compressed to 2 MB)</p>
                </>
              )}
            </div>
            <input
              ref={otherDocFileInputRef}
              type="file"
              className="hidden"
              accept=".pdf,.jpg,.jpeg,.png"
              disabled={otherDocFileProcessing}
              onChange={(e) => {
                const f = e.target.files?.[0];
                e.target.value = '';
                if (f) applyOtherDocFile(f);
              }}
            />
            {otherDocFile && !otherDocFileProcessing && (
              <button
                type="button"
                onClick={() => setOtherDocFile(null)}
                className="mt-2 text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1"
              >
                <FiX size={12} /> Remove file
              </button>
            )}
          </div>

          <GreenButton
            onClick={handleMismatchSubmit}
            loading={loading}
            disabled={
              chequeFileProcessing ||
              otherDocFileProcessing ||
              !accountNumber.trim() ||
              !ifscVerified ||
              !chequeFile
            }
          >
            Submit for Review
          </GreenButton>

          <button
            type="button"
            onClick={handleGoBackFromMismatch}
            className="text-sm text-gray-500 hover:text-gray-700 text-center"
          >
            Go back to bank verification
          </button>
        </div>
      ) : (
        <>
      <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm font-semibold text-gray-700">Investor bank account</p>
          <RiBuilding2Line size={20} className="text-green-600" />
        </div>
        <div className="flex flex-col gap-2.5">
          {bankRadioOptions.map((opt) => (
            <label key={opt.value} className="flex items-center gap-2.5 cursor-pointer group">
              <div
                role="button"
                tabIndex={0}
                onClick={opt.onClick}
                onKeyDown={(e) => e.key === 'Enter' && opt.onClick()}
                className={`rounded-full border-2 flex items-center justify-center transition-all cursor-pointer
                  ${bankMode === opt.value ? 'border-green-600 bg-green-600' : 'border-gray-400 bg-white group-hover:border-green-400'}`}
                style={{ width: 18, height: 18 }}
              >
                {bankMode === opt.value && <div className="w-2 h-2 bg-white rounded-full" />}
              </div>
              <span
                role="button"
                tabIndex={0}
                onClick={opt.onClick}
                onKeyDown={(e) => e.key === 'Enter' && opt.onClick()}
                className={`text-sm cursor-pointer ${bankMode === opt.value ? 'text-gray-900 font-medium' : 'text-gray-500'}`}
              >
                {opt.label}
              </span>
            </label>
          ))}
        </div>
      </div>

      {errors.general && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex gap-2 text-sm text-red-600">
          <FiAlertCircle size={16} className="shrink-0 mt-0.5" /> {errors.general}
        </div>
      )}

      {bankMode === 'existing' && (
        <div className="rounded-xl border border-gray-200 p-5 flex flex-col gap-3">
          {selectedBank ? (
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1">Selected bank</p>
                <p className="text-sm font-semibold text-gray-900">{selectedBank.bank_name || 'Bank account'}</p>
                <p className="text-xs text-gray-500 mt-0.5">
                  {[selectedBank.account_number, selectedBank.ifsc].filter(Boolean).join(' · ')}
                </p>
                {selectedBank.is_verified && (
                  <p className="text-xs text-green-600 flex items-center gap-1 mt-1">
                    <FiCheck size={12} /> Pre-verified via penny drop
                  </p>
                )}
              </div>
              {existingBanks.length >= 1 && (
                <button
                  type="button"
                  onClick={() => setBankSourceModalOpen(true)}
                  className="shrink-0 text-sm font-semibold text-green-700 hover:underline focus:outline-none"
                >
                  Change
                </button>
              )}
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setBankSourceModalOpen(true)}
              className="text-sm font-semibold text-green-700 hover:underline focus:outline-none text-left"
            >
              Choose a bank account →
            </button>
          )}
        </div>
      )}

      {bankMode === 'mobile' && (
        <div className="rounded-xl border border-gray-200 p-5 flex flex-col gap-4">
          {!mobileDigits && (
            <p className="text-sm text-amber-700 bg-amber-50 border border-amber-100 rounded-lg p-3">
              Mobile from onboarding is not available for lookup. Switch to manual entry.
            </p>
          )}
          {mobileBankLoading && (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <svg className="animate-spin h-4 w-4 text-green-500" fill="none" viewBox="0 0 24 24" aria-hidden>
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
              </svg>
              Fetching bank details linked to mobile...
            </div>
          )}
          {mobileBankError && !mobileBankLoading && mobileDigits && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-700 flex gap-2">
              <FiAlertCircle size={16} className="shrink-0 mt-0.5" /> {mobileBankError}
            </div>
          )}
          {mobileBankData && !mobileBankLoading && (
            <>
              <InputField label="Account number">
                <StyledInput value={mobileBankData.account_number} disabled />
              </InputField>
              <InputField label="IFSC">
                <StyledInput value={mobileBankData.ifsc_code} disabled />
              </InputField>
              <InputField label="Bank name">
                <StyledInput value={mobileBankData.bank_name} disabled />
              </InputField>
              {mobileBankData.name_as_per_bank && (
                <InputField label="Name as per bank">
                  <StyledInput value={mobileBankData.name_as_per_bank} disabled />
                </InputField>
              )}
            </>
          )}
        </div>
      )}

      {bankMode === 'manual' && !mismatchData && (
        <div className="rounded-xl border border-gray-200 p-5 flex flex-col gap-4">
          {hasCmrBank && !hasSessionBank && (
            <p className="text-xs text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
              Fields pre-filled from CMR document — edit if needed.
            </p>
          )}
          {hasSessionBank && (
            <p className="text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
              Restored from current session — edit if needed.
            </p>
          )}
          <InputField label="Account number" error={errors.accountNumber}>
            <StyledInput
              value={accountNumber}
              onChange={(e) => {
                setAccountNumber(e.target.value);
                setErrors((p) => ({ ...p, accountNumber: '' }));
              }}
              placeholder="Enter account number"
            />
          </InputField>

          <InputField label="IFSC" error={errors.ifsc}>
            <div className="relative">
              <input
                value={ifscValue}
                onChange={(e) => {
                  setIfsc(e.target.value.toUpperCase());
                  setErrors((p) => ({ ...p, ifsc: '' }));
                }}
                placeholder="e.g. HDFC0001234"
                maxLength={11}
                className={`w-full px-3.5 pr-10 py-2.5 rounded-lg border text-sm transition-all outline-none uppercase
                  ${errors.ifsc ? 'border-red-400' : 'border-gray-300 hover:border-green-400 focus:border-green-500 focus:ring-2 focus:ring-green-500/10'}`}
              />
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                {ifscLookupLoading && (
                  <svg className="animate-spin h-4 w-4 text-green-500" fill="none" viewBox="0 0 24 24" aria-hidden>
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                  </svg>
                )}
                {!ifscLookupLoading && ifscVerified && <FiCheck size={16} className="text-green-500" />}
              </div>
            </div>
          </InputField>

          <InputField label="Bank name" error={errors.bankName}>
            <StyledInput
              value={bankName}
              onChange={(e) => setBankName(e.target.value)}
              placeholder={ifscLookupLoading ? 'Fetching from IFSC...' : 'Auto-filled from IFSC lookup'}
              disabled={ifscLookupLoading}
            />
          </InputField>

          {branchName && (
            <div className="bg-green-50 border border-green-200 rounded-lg px-3.5 py-2.5 text-sm text-green-700">
              Branch: <span className="font-medium">{branchName}</span>
            </div>
          )}
        </div>
      )}

      <GreenButton
        onClick={handleSubmit}
        loading={loading}
        disabled={
          bankMode === 'existing' ? !selectedBank :
          bankMode === 'mobile' ? !mobileDigits || (!mobileBankData && !mobileBankLoading) :
          !accountNumber || !ifscVerified
        }
      >
        Verify &amp; Continue
      </GreenButton>
        </>
      )}
    </div>
  );
}

function InvestorDematStep({ sessionUuid, sessionStatus, onSuccess, prefillDemats = [], selectedDematId, cmrExtraction, isAutomated }) {
  const sessionDemat = sessionStatus?.demat;
  const hasSessionDemat = Boolean(sessionDemat?.demat_number || sessionDemat?.dp_name);

  const existingDemats = useMemo(() => normalizeExistingDemats(prefillDemats), [prefillDemats]);
  const cmrDematFields = useMemo(() => extractDematFromCmr(cmrExtraction), [cmrExtraction]);
  const hasCmrDemat = hasUsableCmrDemat(cmrDematFields);
  const hasPrefill = existingDemats.length > 0;

  // True when there are 2+ saved demats or 1 saved demat whose number differs from CMR
  const showConflict = useMemo(
    () => needsDematSourceResolution(existingDemats, cmrDematFields, sessionDemat),
    [existingDemats, cmrDematFields, sessionDemat]
  );

  // dematMode: 'existing' (calls selectDemat + completeOnboarding) | 'new' (calls uploadCml)
  const [dematMode, setDematMode] = useState(() => {
    if (hasSessionDemat) return 'new';
    if (showConflict) return 'new';
    if (hasPrefill) return 'existing';
    return 'new';
  });

  // Demat picked via modal (or auto-resolved when exactly 1 pool demat, no conflict)
  const [selectedDemat, setSelectedDemat] = useState(() => {
    if (hasSessionDemat || showConflict) return null;
    const id = selectedDematId;
    return existingDemats.find((d) => d.demat_account_id === id) || existingDemats[0] || null;
  });

  const [dematSourceModalOpen, setDematSourceModalOpen] = useState(false);
  const [cmrPreviewUrl, setCmrPreviewUrl] = useState('');
  const modalAutoOpenedRef = useRef(false);

  // Auto-open conflict modal on first render when ambiguous sources exist
  useEffect(() => {
    if (!showConflict || modalAutoOpenedRef.current) return;
    modalAutoOpenedRef.current = true;
    setDematSourceModalOpen(true);
  }, [showConflict]);

  const fileInputRef = useRef(null);
  const [dematNumber, setDematNumber] = useState(() => {
    const raw = hasSessionDemat
      ? (sessionDemat?.demat_number || '')
      : (cmrDematFields?.demat_account_no || '');
    return formatDematInput(raw);
  });
  const [file, setFile] = useState(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!isAutomated || file || !sessionUuid) return undefined;
    let cancelled = false;
    readCmrFileFromIndexedDB(sessionUuid).then((storedFile) => {
      if (!cancelled && storedFile) setFile(storedFile);
    });
    return () => { cancelled = true; };
  }, [isAutomated, file, sessionUuid]);

  const sessionCmrUrl = sessionStatus?.demat?.cmr_document_url;
  useEffect(() => {
    if (!sessionCmrUrl || file) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(sessionCmrUrl);
        if (!res.ok || cancelled) return;
        const blob = await res.blob();
        const filename = sessionCmrUrl.split('/').pop() || 'cmr-document.pdf';
        const f = new File([blob], filename, { type: blob.type });
        if (!cancelled) setFile(f);
      } catch { /* silent — user can re-upload */ }
    })();
    return () => { cancelled = true; };
  }, [sessionCmrUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!file || !isImageFile(file)) { setImagePreviewUrl(null); return undefined; }
    const url = URL.createObjectURL(file);
    setImagePreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  // Modal callbacks
  const handleUseExistingFromModal = (demat) => {
    setSelectedDemat(demat);
    setDematNumber(demat.demat_account_no || '');
    setDematMode('existing');
    setDematSourceModalOpen(false);
  };

  const handleUseCmrFromModal = () => {
    const formatted = formatDematInput(cmrDematFields?.demat_account_no || '');
    setDematNumber(formatted);
    setErrors((p) => ({ ...p, demat: getDematInputError(formatted) || '' }));
    setDematMode('new');
    setDematSourceModalOpen(false);
  };

  const handleUseNewFromModal = () => {
    setDematNumber('');
    setFile(null);
    setErrors((p) => ({ ...p, demat: '' }));
    setDematMode('new');
    setDematSourceModalOpen(false);
  };

  const handleRadioExisting = () => {
    if (showConflict || existingDemats.length >= 2) {
      setDematSourceModalOpen(true);
    } else {
      setDematMode('existing');
      setSelectedDemat(existingDemats[0] || null);
    }
  };

  const handleFileChange = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    if (f.size > 2 * 1024 * 1024) { toast.error('File size must be under 2 MB.'); return; }
    setFile(f);
    setErrors((p) => ({ ...p, file: '' }));
  };

  const dematValidation = validateDematNumber(dematNumber);
  const canSubmitNewDemat = dematValidation.valid && Boolean(file);

  const handleSubmit = async () => {
    const e = {};
    if (dematMode === 'existing') {
      if (!selectedDemat) e.demat = 'Please choose a demat account';
    } else {
      const dematCheck = validateDematNumber(dematNumber);
      if (!dematCheck.valid) e.demat = dematCheck.message;
      if (!file && !isAutomated) e.file = 'Upload CMR / statement document';
      if (!file && isAutomated) e.file = 'CMR document missing. Please re-upload from step 1.';
    }
    setErrors(e);
    if (Object.keys(e).length) return;

    setLoading(true);
    try {
      if (dematMode === 'existing') {
        await investorOnboardingService.selectDemat({
          session_id: sessionUuid,
          demat_account_id: selectedDemat.demat_account_id,
        });
        await investorOnboardingService.completeOnboarding({ session_id: sessionUuid });
      } else {
        const fd = new FormData();
        fd.append('investor_id', sessionStatus?.investor_id);
        fd.append('session_id', sessionUuid);
        fd.append('demat_account_no', dematNumber.trim());
        if (file) fd.append('cml_file', file, file.name || 'cmr-document');
        await investorOnboardingService.uploadCml(fd);
      }
      toast.success('Onboarding complete!');
      onSuccess();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to complete onboarding.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <DematSourceChoiceModal
        open={dematSourceModalOpen}
        existingDemats={existingDemats}
        cmrFields={cmrDematFields}
        hasCmrDemat={hasCmrDemat}
        onUseExisting={handleUseExistingFromModal}
        onUseCmr={handleUseCmrFromModal}
        onUseNew={handleUseNewFromModal}
      />

      <CmrDocumentPreviewModal
        open={Boolean(cmrPreviewUrl)}
        sourceUrl={cmrPreviewUrl}
        title="Saved CMR document"
        onClose={() => setCmrPreviewUrl('')}
      />

      <div>
        <h3 className="text-lg font-semibold text-gray-900">Demat &amp; Documents</h3>
        <p className="text-sm text-gray-500 mt-0.5">
          {showConflict
            ? 'Multiple demat account sources found. Choose which one to use, or enter manually.'
            : 'Demat account number and supporting document (PDF, JPG, PNG — max 2 MB).'}
        </p>
      </div>

      {/* Conflict banner — same pattern as IFA portal */}
      {showConflict && (
        <div className="flex items-center justify-between rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
          <p className="text-sm text-amber-800">
            {existingDemats.length >= 2
              ? `${existingDemats.length} saved demat accounts found${cmrDematFields?.demat_account_no ? ' + CMR data' : ''}. Resolve which one to use.`
              : 'Saved demat conflicts with CMR data. Choose which one to prefill.'}
          </p>
          <button
            type="button"
            onClick={() => setDematSourceModalOpen(true)}
            className="ml-4 shrink-0 text-sm font-semibold text-green-700 hover:underline focus:outline-none"
          >
            Choose source
          </button>
        </div>
      )}

      {hasPrefill && (
        <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
          <p className="text-sm font-semibold text-gray-700 mb-3">Investor demat account</p>
          <div className="flex flex-col gap-2.5">
            {[
              {
                value: 'existing',
                label: showConflict
                  ? existingDemats.length >= 2
                    ? `Select from ${existingDemats.length} saved accounts${hasCmrDemat ? ' or CMR' : ''}`
                    : 'Resolve demat conflict — saved vs CMR'
                  : 'Use a saved demat account',
                onClick: handleRadioExisting,
              },
              {
                value: 'new',
                label: hasSessionDemat
                  ? 'Use session demat (pre-filled)'
                  : cmrDematFields?.demat_account_no
                    ? 'Use CMR-extracted demat (pre-filled)'
                    : 'Enter demat manually',
                onClick: () => setDematMode('new'),
              },
            ].map((opt) => (
              <label key={opt.value} className="flex items-center gap-2.5 cursor-pointer group">
                <div
                  role="button"
                  tabIndex={0}
                  onClick={opt.onClick}
                  onKeyDown={(e) => e.key === 'Enter' && opt.onClick()}
                  className={`rounded-full border-2 flex items-center justify-center transition-all cursor-pointer
                    ${dematMode === opt.value ? 'border-green-600 bg-green-600' : 'border-gray-400 bg-white group-hover:border-green-400'}`}
                  style={{ width: 18, height: 18 }}
                >
                  {dematMode === opt.value && <div className="w-2 h-2 bg-white rounded-full" />}
                </div>
                <span
                  role="button"
                  tabIndex={0}
                  onClick={opt.onClick}
                  onKeyDown={(e) => e.key === 'Enter' && opt.onClick()}
                  className={`text-sm cursor-pointer ${dematMode === opt.value ? 'text-gray-900 font-medium' : 'text-gray-500'}`}
                >
                  {opt.label}
                </span>
              </label>
            ))}
          </div>
        </div>
      )}

      {dematMode === 'existing' && (
        <div className="rounded-xl border border-gray-200 p-5 flex flex-col gap-3">
          {selectedDemat ? (
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1">Selected demat</p>
                <p className="text-sm font-semibold text-gray-900">{selectedDemat.dp_name || 'Demat account'}</p>
                <p className="text-xs text-gray-500 mt-0.5">
                  {[selectedDemat.demat_account_no, selectedDemat.status].filter(Boolean).join(' · ')}
                </p>
                {selectedDemat.cmr_document_url && (
                  <button
                    type="button"
                    onClick={() => setCmrPreviewUrl(selectedDemat.cmr_document_url)}
                    className="mt-1.5 text-xs font-semibold text-green-700 hover:underline focus:outline-none"
                  >
                    View CMR document →
                  </button>
                )}
              </div>
              {existingDemats.length >= 1 && (
                <button
                  type="button"
                  onClick={() => setDematSourceModalOpen(true)}
                  className="shrink-0 text-sm font-semibold text-green-700 hover:underline focus:outline-none"
                >
                  Change
                </button>
              )}
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setDematSourceModalOpen(true)}
              className="text-sm font-semibold text-green-700 hover:underline focus:outline-none text-left"
            >
              Choose a demat account →
            </button>
          )}
          {errors.demat && (
            <p className="text-xs text-red-500 flex items-center gap-1">
              <FiAlertCircle size={12} /> {errors.demat}
            </p>
          )}
        </div>
      )}

      {dematMode === 'new' && (
        <div className="flex flex-col gap-5">
          {hasSessionDemat && (
            <p className="text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
              Restored from current session — edit if needed.
            </p>
          )}
          {!hasSessionDemat && cmrDematFields?.demat_account_no && (
            <p className="text-xs text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
              Demat number pre-filled from CMR document — edit if needed.
            </p>
          )}
          <InputField label="Demat number" error={errors.demat}>
            <StyledInput
              value={dematNumber}
              onChange={(e) => {
                const { value, error } = processDematInputChange(e.target.value);
                setDematNumber(value);
                setErrors((p) => ({ ...p, demat: error || '' }));
              }}
              placeholder="IN12345678901234 or 16-digit CDSL"
              maxLength={DEMAT_MAX_LENGTH}
            />
            <p className="mt-1 text-xs text-gray-500">{DEMAT_FORMAT_HINT}</p>
          </InputField>

          <div>
            <p className="text-sm font-medium text-gray-700 mb-1">CMR / document</p>
            {errors.file && (
              <p className="text-xs text-red-500 flex items-center gap-1 mb-1">
                <FiAlertCircle size={12} /> {errors.file}
              </p>
            )}
            <div
              role="button"
              tabIndex={0}
              onClick={() => !loading && fileInputRef.current?.click()}
              onKeyDown={(e) => e.key === 'Enter' && fileInputRef.current?.click()}
              className={`border-2 border-dashed rounded-lg p-4 flex flex-col items-center gap-2 cursor-pointer transition-all
                ${file ? 'border-green-400 bg-green-50/50' : 'border-gray-200 bg-gray-50 hover:border-green-400 hover:bg-green-50/30'}`}
            >
              {file ? (
                <div className="flex flex-col items-center gap-3 w-full">
                  {imagePreviewUrl ? (
                    <div className="w-full rounded-lg overflow-hidden border border-green-200/80 bg-gray-50 max-h-52 flex items-center justify-center">
                      <img
                        src={imagePreviewUrl}
                        alt={`Preview: ${file.name}`}
                        className="max-h-52 w-full object-contain"
                      />
                    </div>
                  ) : (
                    <div
                      className="w-20 h-20 rounded-xl bg-green-100 border border-green-200/70 flex items-center justify-center"
                      aria-hidden
                    >
                      <HiOutlineDocumentText className="w-10 h-10 text-green-700" />
                    </div>
                  )}
                  <div className="text-center min-w-0 w-full">
                    <p className="text-sm text-gray-700 font-medium break-all">{file.name}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{(file.size / 1024).toFixed(1)} KB</p>
                  </div>
                </div>
              ) : (
                <>
                  <FiUpload size={20} className="text-gray-400" />
                  <p className="text-sm text-gray-500">Click to select file</p>
                  <p className="text-xs text-gray-400">PDF, JPG, PNG — max 2 MB</p>
                </>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={handleFileChange}
            />
            {file && (
              <button
                type="button"
                onClick={() => setFile(null)}
                className="mt-2 text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1"
              >
                <FiX size={12} /> Remove file
              </button>
            )}
          </div>
        </div>
      )}

      <GreenButton
        onClick={handleSubmit}
        loading={loading}
        disabled={dematMode === 'existing' ? !selectedDemat : !canSubmitNewDemat}
      >
        Complete onboarding
      </GreenButton>
    </div>
  );
}

function InvestorCompletePanel({ contextLabel, onViewList, onAnother }) {
  const checklist = [
    'Mobile number verified and linked to the investor profile.',
    'PAN, bank account and demat details have been recorded.',
    'CMR/CML document uploaded and onboarding is complete.',
  ];

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col items-center text-center">
        <div className="w-18 h-18 rounded-full bg-green-600 flex items-center justify-center mb-5 shadow-lg shadow-green-600/30 ring-4 ring-green-100">
          <FiCheck className="w-10 h-10 text-white" strokeWidth={2.5} />
        </div>
        <h3 className="text-xl font-bold text-gray-900">Investor created successfully!</h3>
        <p className="text-sm text-gray-500 mt-2 max-w-md leading-relaxed">
          Investor onboarded successfully! The investor profile has been saved and is ready for further action.
        </p>
        {contextLabel && (
          <p className="text-xs text-gray-400 mt-2">
            Partner: <span className="font-medium text-gray-600">{contextLabel}</span>
          </p>
        )}
      </div>

      <div className="rounded-xl border border-gray-100 bg-gray-50/90 px-4 py-4 text-left">
        <ul className="space-y-3">
          {checklist.map((line) => (
            <li key={line} className="flex gap-3 text-sm text-gray-700">
              <span className="shrink-0 w-5 h-5 rounded-full bg-green-100 flex items-center justify-center mt-0.5">
                <FiCheck className="w-3 h-3 text-green-600" strokeWidth={3} />
              </span>
              <span className="leading-snug">{line}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="flex flex-col gap-3">
        <button
          type="button"
          onClick={onViewList}
          className="w-full py-3 px-4 rounded-xl font-semibold text-sm bg-green-600 text-white hover:bg-green-700 transition-all shadow-md"
        >
          Go to investors list
        </button>
        <button
          type="button"
          onClick={onAnother}
          className="w-full py-2.5 px-4 rounded-xl font-medium text-sm border border-gray-200 text-gray-700 bg-white hover:bg-gray-50 transition-all"
        >
          Onboard another investor
        </button>
      </div>
    </div>
  );
}

export default function InvestorOnboardingPage({ onBackToMethodSelection }) {
  const { sessionUuid: sessionUuidParam } = useParams();
  const navigate = useNavigate();

  const isAutomated = getStoredInvestorOnboardingType() === INVESTOR_ONBOARDING_TYPES.AUTOMATED;
  const wizardSteps = useMemo(() => getWizardSteps(isAutomated), [isAutomated]);
  const cmrExtraction = useMemo(
    () => (isAutomated ? readStoredCmrExtraction() : null),
    [isAutomated, sessionUuidParam]
  );

  const [phase, setPhase] = useState(sessionUuidParam ? 'loading' : 'start');
  const [selectedIfaId, setSelectedIfaId] = useState('');
  const [selectedIfaLabel, setSelectedIfaLabel] = useState('');
  const [starting, setStarting] = useState(false);

  const [sessionStatus, setSessionStatus] = useState(null);
  const [currentStepKey, setCurrentStepKey] = useState('STEP_1_PARTNER_SELECTION');
  const [completedStepKeys, setCompletedStepKeys] = useState([]);
  const [savedMobile, setSavedMobile] = useState('');
  const [initialMobile, setInitialMobile] = useState('');
  const [donePhase, setDonePhase] = useState(false);
  const [prefillData, setPrefillData] = useState(null);
  // overrideStepKey: set when admin clicks a completed step to go back and edit it
  const [overrideStepKey, setOverrideStepKey] = useState(null);
  // Modal for 409 duplicate folio on start-mobile
  const [startAlreadyOnboardedModal, setStartAlreadyOnboardedModal] = useState(null);

  const handleGoBack = (stepKey) => {
    // Clicking the real current server-side step always clears the override (returns to current)
    if (stepKey === currentStepKey) {
      setOverrideStepKey(null);
    } else {
      setOverrideStepKey(stepKey);
    }
  };

  const loadStatus = useCallback(async (uuid, opts = {}) => {
    const silent = opts.silent === true;
    try {
      const data = await investorOnboardingService.getStatus(uuid);
      setSessionStatus(data);
      if (data.prefill) {
        setPrefillData(data.prefill);
      }
      const wizardStep = mapApiStepToWizard(data.current_step);
      setCurrentStepKey(wizardStep);

      const apiDone = (data.completed_steps || []).map((s) => mapCompletedStepKey(s.step || s.key));
      let keys = [...new Set(apiDone)];
      if (wizardStep === 'STEP_COMPLETED') {
        const prior = getWizardSteps(isAutomated).filter((s) => s.key !== 'STEP_COMPLETED').map((s) => s.key);
        keys = [...new Set([...keys, ...prior])];
      }
      setCompletedStepKeys(keys);

      const m = data.mobile;
      if (m && /^\d{10}$/.test(String(m))) {
        setSavedMobile(String(m));
      }

      if (wizardStep === 'STEP_COMPLETED') {
        setDonePhase(true);
      } else if (uuid) {
        patchOnboardingState({
          sessionUuid: uuid,
          onboardingType: getStoredInvestorOnboardingType() || inferInvestorOnboardingType(),
        });
      }

      setPhase('wizard');
      return data;
    } catch (err) {
      if (!silent) {
        toast.error(err?.response?.data?.message || 'Failed to load session.');
        clearLocalOnboardingState();
        navigate('/investors/onboard', { replace: true });
        throw err;
      }
      return null;
    }
  }, [navigate, isAutomated]);

  useEffect(() => {
    if (!sessionUuidParam) return;
    patchOnboardingState({
      sessionUuid: sessionUuidParam,
      onboardingType: getStoredInvestorOnboardingType() || inferInvestorOnboardingType(),
    });
  }, [sessionUuidParam]);

  useEffect(() => {
    if (!sessionUuidParam) {
      setPhase('start');
      return;
    }

    setPhase('loading');
    loadStatus(sessionUuidParam).catch(() => {});
  }, [sessionUuidParam, loadStatus]);

  const handleSelectIfa = (id, partner) => {
    setSelectedIfaId(id);
    if (partner) {
      setSelectedIfaLabel(
        partner.name || partner.email || partner.full_name || `Partner ${id}`
      );
    } else if (!id) {
      setSelectedIfaLabel('');
    }
  };

  const handleStart = async () => {
    if (!selectedIfaId || initialMobile.length !== 10) return;
    setStarting(true);
    try {
      const data = await investorOnboardingService.startMobile({ ifa_id: selectedIfaId, mobile: initialMobile });
      const sessionId = data.session_uuid || data.session_id; // Support both just in case
      patchOnboardingState({
        sessionUuid: sessionId,
        onboardingType: inferInvestorOnboardingType(),
      });
      await promotePendingCmrFileToSession(sessionId);
      toast.success('Session started.');
      navigate(`/investors/onboard/${sessionId}`, { replace: true });
    } catch (err) {
      const status = err?.response?.status;
      const message = err?.response?.data?.message || 'Failed to start onboarding.';
      if (status === 409) {
        const body = err?.response?.data || {};
        setStartAlreadyOnboardedModal({
          message,
          folioNumber: body?.data?.folio_number || body?.data?.folio_id || null,
          pan: body?.data?.pan || null,
        });
      } else {
        toast.error(message);
      }
    } finally {
      setStarting(false);
    }
  };

  const advanceAfterStep = useCallback(async () => {
    if (sessionUuidParam) await loadStatus(sessionUuidParam, { silent: true });
    setOverrideStepKey(null); // Always clear override after any step saves successfully
  }, [sessionUuidParam, loadStatus]);

  const displayPercent =
    donePhase || currentStepKey === 'STEP_COMPLETED'
      ? 100
      : sessionStatus?.percentage_complete ?? 0;

  // Prefer ifa_name (resolved name from API) over raw ifa_id fallback
  const contextLabel = selectedIfaLabel || sessionStatus?.ifa_name || '';

  const renderForm = () => {
    if (donePhase || currentStepKey === 'STEP_COMPLETED') {
      return (
        <InvestorCompletePanel
          contextLabel={contextLabel}
          onViewList={() => {
            clearLocalOnboardingState();
            navigate('/investors/list');
          }}
          onAnother={() => {
            clearLocalOnboardingState();
            setDonePhase(false);
            setSessionStatus(null);
            navigate('/investors/onboard', { replace: true });
          }}
        />
      );
    }

    if (!sessionUuidParam) return null;

    // Which step form to display: override (going back) or real current
    const activeStep = overrideStepKey ?? currentStepKey;

    switch (activeStep) {
      case 'STEP_1_PARTNER_SELECTION':
        return (
          <IFAUpdateMobileStep
            sessionUuid={sessionUuidParam}
            contextLabel={contextLabel}
            mobile={sessionStatus?.mobile || ''}
            onSuccess={advanceAfterStep}
          />
        );
      case 'STEP_2_PAN':
        return (
          <InvestorPersonalStep
            key={activeStep}
            sessionUuid={sessionUuidParam}
            sessionStatus={sessionStatus}
            onSuccess={advanceAfterStep}
            onSaveMobile={setSavedMobile}
            setPrefillData={setPrefillData}
            cmrExtraction={cmrExtraction}
          />
        );
      case 'STEP_3_BANK_DETAILS':
        return (
          <InvestorBankStep
            key={activeStep}
            sessionUuid={sessionUuidParam}
            sessionStatus={sessionStatus}
            mobile={savedMobile || sessionStatus?.mobile || ''}
            onSuccess={advanceAfterStep}
            prefillBanks={sessionStatus?.existing_banks || []}
            selectedBankId={sessionStatus?.prefill?.selected_bank_id || null}
            cmrExtraction={cmrExtraction}
          />
        );
      case 'STEP_4_DEMAT':
        return (
          <InvestorDematStep
            key={activeStep}
            sessionUuid={sessionUuidParam}
            sessionStatus={sessionStatus}
            onSuccess={advanceAfterStep}
            prefillDemats={sessionStatus?.existing_demat || []}
            selectedDematId={sessionStatus?.prefill?.selected_demat_id || null}
            cmrExtraction={cmrExtraction}
            isAutomated={isAutomated}
          />
        );
      default:
        return (
          <div className="flex flex-col gap-4 items-center text-center py-8">
            <FiAlertCircle className="w-10 h-10 text-amber-500" />
            <p className="text-sm text-gray-700">Step: {activeStep}</p>
            <GreenButton onClick={() => loadStatus(sessionUuidParam)}>Refresh status</GreenButton>
          </div>
        );
    }
  };

  if (phase === 'loading' && sessionUuidParam) {
    return (
      <DashboardPageWrapper>
        <div className="flex items-center justify-center min-h-0 py-16 sm:min-h-[60vh]">
          <div className="flex flex-col items-center gap-4">
            <svg className="animate-spin h-10 w-10 text-green-500" fill="none" viewBox="0 0 24 24" aria-hidden>
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
            </svg>
            <p className="text-sm text-gray-500">Loading session…</p>
          </div>
        </div>
      </DashboardPageWrapper>
    );
  }

  if (!sessionUuidParam) {
    return (
      <OnboardingWizardLayout
        steps={wizardSteps}
        completedStepKeys={[]}
        currentStepKey="STEP_1_PARTNER_SELECTION"
        contextLabel=""
        percentComplete={0}
        onBackToList={() => {
          if (onBackToMethodSelection) {
            onBackToMethodSelection();
          } else {
            backToMethodSelection();
            navigate('/investors/onboard');
          }
        }}
      >
        <AlreadyOnboardedModal
          open={Boolean(startAlreadyOnboardedModal)}
          message={startAlreadyOnboardedModal?.message}
          folioNumber={startAlreadyOnboardedModal?.folioNumber}
          pan={startAlreadyOnboardedModal?.pan}
          onClose={() => setStartAlreadyOnboardedModal(null)}
          onGoToInvestors={() => {
            setStartAlreadyOnboardedModal(null);
            navigate('/investors/list');
          }}
        />
        <IFAStartStep
          selectedIfaId={selectedIfaId}
          onSelectIfa={handleSelectIfa}
          mobile={initialMobile}
          onMobileChange={setInitialMobile}
          onStart={handleStart}
          starting={starting}
          isAutomated={isAutomated}
        />
      </OnboardingWizardLayout>
    );
  }

  return (
    <OnboardingWizardLayout
      steps={wizardSteps}
      completedStepKeys={completedStepKeys}
      currentStepKey={overrideStepKey ?? currentStepKey}
      maxReachedKey={currentStepKey}
      contextLabel={contextLabel}
      percentComplete={displayPercent}
      expiresAt={sessionStatus?.expires_at}
      onBackToList={() => navigate('/investors/list')}
      onStepClick={handleGoBack}
    >
      {renderForm()}
    </OnboardingWizardLayout>
  );
}
